import { createWebSocketConnection } from "./webSocketFactory";

/**
 * Emite um evento via WebSocket e desconecta após a emissão
 * Ideal para emissões pontuais que não requerem listeners
 *
 * @param event Nome do evento a ser emitido
 * @param data Dados a serem enviados
 * @param waitForResponse Se deve aguardar confirmação de conexão antes de emitir
 * @returns Promise que resolve quando a emissão é concluída
 *
 * @example
 * ```ts
 * await emitWebSocketEvent("createCheckInCall", checkinData);
 * ```
 */
export async function emitWebSocketEvent(event: string, data: unknown, waitForResponse = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const socket = createWebSocketConnection();

    // Timeout de segurança
    const timeout = setTimeout(() => {
      socket.disconnect();
      reject(new Error("WebSocket emit timeout"));
    }, 5000);

    socket.on("connect_error", (error: Error) => {
      clearTimeout(timeout);
      socket.disconnect();
      reject(error);
    });

    if (waitForResponse) {
      // Aguarda a conexão antes de emitir
      socket.on("connect", () => {
        socket.emit(event, data);
        clearTimeout(timeout);

        // Aguarda um pouco para garantir que a emissão foi processada
        setTimeout(() => {
          socket.disconnect();
          resolve();
        }, 100);
      });
    } else {
      // Emite imediatamente após conectar
      socket.on("connect", () => {
        socket.emit(event, data);
        clearTimeout(timeout);
        socket.disconnect();
        resolve();
      });
    }

    // Inicia a conexão
    socket.connect();
  });
}

/**
 * Emite um evento via WebSocket com callback de resposta
 * Útil quando você precisa aguardar uma resposta do servidor
 *
 * @param event Nome do evento a ser emitido
 * @param data Dados a serem enviados
 * @param responseEvent Nome do evento de resposta esperado
 * @param timeout Tempo máximo de espera em ms (padrão: 5000)
 * @returns Promise que resolve com a resposta do servidor
 *
 * @example
 * ```ts
 * const response = await emitWebSocketEventWithResponse(
 *   "createCheckInCall",
 *   checkinData,
 *   "checkInCallResponse"
 * );
 * ```
 */
export async function emitWebSocketEventWithResponse<T = unknown>(event: string, data: unknown, responseEvent: string, timeout = 5000): Promise<T> {
  return new Promise((resolve, reject) => {
    const socket = createWebSocketConnection();

    const timeoutId = setTimeout(() => {
      socket.off(responseEvent);
      socket.disconnect();
      reject(new Error(`WebSocket response timeout for event: ${responseEvent}`));
    }, timeout);

    socket.on("connect_error", (error: Error) => {
      clearTimeout(timeoutId);
      socket.disconnect();
      reject(error);
    });

    socket.on("connect", () => {
      // Registra o listener de resposta
      socket.on(responseEvent, (response: T) => {
        clearTimeout(timeoutId);
        socket.off(responseEvent);
        socket.disconnect();
        resolve(response);
      });

      // Emite o evento
      socket.emit(event, data);
    });

    // Inicia a conexão
    socket.connect();
  });
}
